import React from 'react';
import {DeleteOutlined, EditOutlined, PlusOutlined} from '@ant-design/icons';
import {Button, Divider, Modal, Row, Table, Typography} from 'antd';
import PageTitle from '../../../components/page/PageTitle';

import {useDeleteAccessoryMutation, useGetAccessoriesQuery} from '../../../store/api/carsApi';
import AccessoryDrawer from '../../../components/car/AccessoryDrawer';
import ErrorBoundary from '../../../components/ErrorBoundary';
import {messageType, showMessages} from '../../../helpers/message';
import {Accessory} from '../../../types/carTypes';
import {BaseError} from '../../../types/apiTypes';

import './AccessoriesView.less';

const {confirm} = Modal;
const {Title} = Typography;

const AccessoriesView = () => {
    const mobileMode = window.innerWidth < 500;

    const [currentAccessory, setCurrentAccessory] = React.useState<Accessory | undefined>(undefined);
    const [action, setAction] = React.useState<'Add' | 'Update'>('Add');
    const [drawerOpen, setDrawerOpen] = React.useState(false);

    const [page, setPage] = React.useState(1);
    const [perPage, setPerPage] = React.useState(10);
    const [total, setTotal] = React.useState(10);

    const {data: accessories, isLoading: accessoriesIsLoading, isError, error, isSuccess, isFetching} = useGetAccessoriesQuery({page, perPage});
    const [deleteAccessory, {isLoading: deleteIsLoading, isSuccess: deleteSuccess, isError: deleteIsError, error: deleteError, reset: deleteReset}] = useDeleteAccessoryMutation();

    React.useEffect(() => {
        if (isSuccess && accessories) {
            setTotal(accessories.total);
        } else if (isError && error) {
            showMessages((error as BaseError).data?.message, messageType.error);
        }
    }, [accessories, error, isError, isSuccess]);

    React.useEffect(() => {
        if (deleteSuccess && currentAccessory) {
            showMessages(`Accessory ${currentAccessory.name} successfully deleted`, messageType.success);
            setCurrentAccessory(undefined);
            deleteReset();
        } else if (deleteIsError) {
            showMessages((deleteError as BaseError).data?.message, messageType.error);
            deleteReset();
        }
    }, [currentAccessory, deleteError, deleteIsError, deleteReset, deleteSuccess]);

    const onEdit = (accessory: Accessory) => {
        setAction('Update')
        setCurrentAccessory(accessory);
        setDrawerOpen(true);
    }

    const onDelete = (accessory: Accessory) => {
        // Set current accessory to show name in delete success message.
        setCurrentAccessory(accessory);
        confirm({
            title: 'Are you sure you want to delete this accessory?',
            content: `${accessory.car_count} - ${accessory.name}`,
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            maskClosable: true,
            onOk: () => deleteAccessory(accessory.id),
            onCancel: () => setCurrentAccessory(undefined),
        });
    }

    const handleClose = () => {
        setDrawerOpen(false);
        setCurrentAccessory(undefined);
        setAction('Add');
    }

    const pageChange = (page: number, perPage: number) => {
        setPage(page);
        setPerPage(perPage);
    }

    const renderActionsColumn = (_text: string, record: Accessory) => {
        return (
            <>
                <EditOutlined key="edit" onClick={() => onEdit(record)}/>
                <Divider type="vertical"/>
                <DeleteOutlined key="delete" onClick={() => onDelete(record)}/>
            </>
        )
    }

    const renderExpandedMobileRow = (record: Accessory) => {
        // Makes car_count description an expandable row in mobile.
        return [
            <li key={0} className="accessory-car-count-row-expanded">
                {record.car_count}
            </li>,
        ];
    }

    let columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            dataIndex: 'car_count',
            key: 'description',
            className: "accessory-car-count-row",
            align: 'right' as 'right', // Antd 'align' known type bug, need to force with 'as'.
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: '90px',
            colSpan: 2,
            render: renderActionsColumn,
        }
    ];
    // Moves car_count column to expandable row on mobile.
    columns = mobileMode ? [columns[0], columns[2]] : columns;

    const paginationAttr = {
        onChange: pageChange,
        showSizeChanger: true,
        onShowSizeChange: pageChange,
        total,
        perPage,
    }

    const AccessoriesHeader = (
        <Row className="accessories-header-row">
            <Title className="accessories-list-title" level={5}>List of Accessories</Title>
            <Button
                type="primary"
                icon={<PlusOutlined/>}
                onClick={() => setDrawerOpen(true)}
            >
                {window.innerWidth > 350 ? "Add New" : ""}
            </Button>
        </Row>
    );

    return (
        <ErrorBoundary>
            <div className="title-wrapper">
                <PageTitle title="Accessories"/>
            </div>
            <AccessoryDrawer
                accessory={currentAccessory}
                action={action}
                drawerOpen={drawerOpen}
                handleClose={handleClose}
            />
            <Table
                rowKey="id"
                caption={AccessoriesHeader}
                dataSource={accessories?.data}
                expandable={{expandedRowRender: mobileMode ? renderExpandedMobileRow : undefined}}
                columns={columns}
                loading={accessoriesIsLoading || deleteIsLoading || isFetching}
                pagination={paginationAttr}
            />
        </ErrorBoundary>
    );
}

export default AccessoriesView;
